/**
 * Hook for streaming chat with reasoning steps and agent activity
 * 
 * Integrates Vercel AI SDK useChat with SSE adapter and manages
 * reasoning state separately from message content.
 */

import { useChat as useVercelChat } from "@ai-sdk/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { createSSEAdapter } from "@/lib/chat-stream-adapter";
import type { UIResource } from "@/components/application/ai-chat/ui-resource-renderer";

export interface ThinkingStep {
    id: string;
    step_type: "thinking" | "tool_call" | "rag_search" | "delegate" | "response" | "compaction";
    content: string;
    timestamp: string;
    agent_name: string;
    metadata?: Record<string, any>;
}

export interface ToolCall {
    id: string;
    tool_name: string;
    input?: any;
    output?: any;
    agent_name: string;
    timestamp: string;
}

export interface AgentDelegation {
    id: string;
    from_agent: string;
    to_agent: string;
    reason: string;
    timestamp: string;
}

export interface UseChatStreamOptions {
    conversationId: string | (() => string | null);
    enableRag?: boolean;
    knowledgeBaseId?: string;
    minSimilarity?: number;
    maxResults?: number;
    onError?: (error: Error) => void;
    getFiles?: () => File[] | null; // Function to get current files for attachment
    getDocumentIds?: () => string[] | null; // Function to get current document IDs for attachment
    onConversationTitle?: (conversationId: string, title: string) => void;
}

export interface UseChatStreamReturn {
    // Vercel AI SDK chat methods
    messages: Array<{
        id: string;
        role: "user" | "assistant";
        content: string;
    }>;
    input: string;
    setInput: (input: string) => void;
    setMessages: (messages: Array<{ id: string; role: "user" | "assistant"; content: string }>) => void;
    handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
    append: (message: { role: "user" | "assistant"; content: string }) => void;
    isLoading: boolean;
    stop: () => void;
    reload: () => void;
    
    // Reasoning state
    currentAgent: string | null;
    thinkingSteps: ThinkingStep[];
    activeToolCalls: ToolCall[];
    agentDelegations: AgentDelegation[];
    
    // UI Resources
    uiResources: UIResource[];
    
    // Streaming state
    isStreaming: boolean;
    streamError: Error | null;
}

export function useChatStream(options: UseChatStreamOptions): UseChatStreamReturn {
    const { conversationId: conversationIdOrGetter, enableRag = true, onError, getFiles, getDocumentIds } = options;
    
    // Get conversation ID (support both string and function)
    const getConversationId = useCallback(() => {
        if (typeof conversationIdOrGetter === 'function') {
            return conversationIdOrGetter();
        }
        return conversationIdOrGetter;
    }, [conversationIdOrGetter]);
    
    // Reasoning state
    const [currentAgent, setCurrentAgent] = useState<string | null>(null);
    const [thinkingSteps, setThinkingSteps] = useState<ThinkingStep[]>([]);
    const [activeToolCalls, setActiveToolCalls] = useState<ToolCall[]>([]);
    const [agentDelegations, setAgentDelegations] = useState<AgentDelegation[]>([]);
    const [uiResources, setUIResources] = useState<UIResource[]>([]);
    const [streamError, setStreamError] = useState<Error | null>(null);
    
    // Ref to track if we're currently processing a stream
    const isProcessingRef = useRef(false);
    const abortControllerRef = useRef<AbortController | null>(null);
    
    // Create SSE adapter - Vercel AI SDK expects a function that takes Request and returns Response
    const sseAdapter = useCallback(
        async (request: Request): Promise<Response> => {
            // Get current conversation ID (dynamic)
            const currentConversationId = getConversationId();
            if (!currentConversationId) {
                throw new Error("Conversation ID is required but not available");
            }
            
            // Store abort controller for stop functionality
            if (request.signal) {
                abortControllerRef.current = request.signal as any;
            }
            
            isProcessingRef.current = true;
            setStreamError(null);
            
            try {
                // Create callbacks for SSE events
                const sseCallbacks = {
                    onThinkingStep: (step: {
                        step_type: string;
                        content: string;
                        agent_name: string;
                        timestamp: string;
                        metadata?: Record<string, any>;
                    }) => {
                        // Generate unique ID for the step
                        const stepId = `${step.timestamp}-${Date.now()}-${Math.random()}`;
                        setThinkingSteps(prev => [...prev, {
                            id: stepId,
                            step_type: step.step_type as ThinkingStep['step_type'],
                            content: step.content,
                            timestamp: step.timestamp,
                            agent_name: step.agent_name,
                            metadata: step.metadata
                        }]);
                        // Update current agent
                        if (step.agent_name) {
                            setCurrentAgent(step.agent_name);
                        }
                    },
                    onToolCall: (tool: {
                        tool_name: string;
                        input?: any;
                        output?: any;
                        agent_name: string;
                    }) => {
                        const toolId = `${Date.now()}-${Math.random()}`;
                        setActiveToolCalls(prev => [...prev, {
                            id: toolId,
                            tool_name: tool.tool_name,
                            input: tool.input,
                            output: tool.output,
                            agent_name: tool.agent_name,
                            timestamp: new Date().toISOString()
                        }]);
                    },
                    onAgentSwitch: (delegation: {
                        from_agent: string;
                        to_agent: string;
                        reason: string;
                    }) => {
                        const delegationId = `${Date.now()}-${Math.random()}`;
                        setAgentDelegations(prev => [...prev, {
                            id: delegationId,
                            from_agent: delegation.from_agent,
                            to_agent: delegation.to_agent,
                            reason: delegation.reason,
                            timestamp: new Date().toISOString()
                        }]);
                        setCurrentAgent(delegation.to_agent);
                    },
                    onUIResource: (resource: UIResource) => {
                        // Add UI resource to the list
                        setUIResources(prev => [...prev, resource]);
                    },
                    onConversationTitle: (data: { conversation_id: string; title: string }) => {
                        if (options.onConversationTitle) {
                            options.onConversationTitle(data.conversation_id, data.title);
                        }
                    },
                };
                
                const adapter = createSSEAdapter(currentConversationId, sseCallbacks, getFiles, getDocumentIds);
                
                const response = await adapter(request);
                
                // Also parse reasoning events from the same stream
                // We need to create a separate reader for reasoning events
                // For now, we'll handle this in the response processing
                
                return response;
            } catch (error) {
                isProcessingRef.current = false;
                const err = error instanceof Error ? error : new Error(String(error));
                setStreamError(err);
                
                if (onError) {
                    onError(err);
                }
                throw error;
            }
        },
        [getConversationId, onError]
    );
    
    // Use Vercel AI SDK useChat
    // IMPORTANT: Vercel AI SDK's useChat expects 'api' to be a string URL, not a function
    // We need to create an API route endpoint that handles the request
    // For now, let's try wrapping the adapter in a way that Vercel AI SDK can use
    const apiAdapter = useCallback(
        async (input: URL | RequestInfo, init?: RequestInit): Promise<Response> => {
            // Convert input to Request object
            let request: Request;
            if (input instanceof Request) {
                request = input;
            } else {
                // Convert URL or string to Request
                const url = input instanceof URL ? input.toString() : input;
                request = new Request(url, init);
            }
            
            // Call our SSE adapter
            return sseAdapter(request);
        },
        [sseAdapter]
    );
    
    const {
        messages: vercelMessages,
        input,
        setInput,
        setMessages,
        handleSubmit: vercelHandleSubmit,
        append: vercelAppend,
        isLoading,
        stop: vercelStop,
        reload,
    } = useVercelChat({
        api: '/api/chat', // Dummy URL - we'll intercept with fetch
        fetch: apiAdapter, // Override fetch to use our adapter
        body: {
            enable_rag: enableRag,
            knowledge_base_id: options.knowledgeBaseId,
            min_similarity: options.minSimilarity,
            max_results: options.maxResults,
        },
        onFinish: () => {
            isProcessingRef.current = false;
            // Don't reset currentAgent here, keep it for display
        },
        onError: (error) => {
            isProcessingRef.current = false;
            setStreamError(error);
            if (onError) {
                onError(error);
            }
        },
    });
    
    // Reset thinking steps when starting a new message
    useEffect(() => {
        if (isLoading && !isProcessingRef.current) {
            setThinkingSteps([]);
            setActiveToolCalls([]);
            setAgentDelegations([]);
            setUIResources([]);
            setCurrentAgent(null);
        }
    }, [isLoading]);
    
    // Enhanced stop function
    const stop = useCallback(() => {
        vercelStop();
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        isProcessingRef.current = false;
    }, [vercelStop]);
    
    // Enhanced handleSubmit that also processes reasoning events
    const handleSubmit = useCallback(
        async (e: React.FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            
            // Reset reasoning state
            setThinkingSteps([]);
            setActiveToolCalls([]);
            setAgentDelegations([]);
            setUIResources([]);
            setStreamError(null);
            setCurrentAgent(null);
            
            // Call Vercel's handleSubmit
            vercelHandleSubmit(e);
            
            // Note: Reasoning events will be processed separately
            // when we implement the full SSE parsing
        },
        [vercelHandleSubmit]
    );
    
    // Process reasoning events from SSE stream
    // This would ideally be done in parallel with the content stream
    // For now, we'll need to enhance the adapter to emit both streams
    useEffect(() => {
        // This effect will be enhanced when we have full SSE event parsing
        // For now, it's a placeholder
    }, []);
    
    // Clean response content by removing thinking steps and formatting markers
    const cleanContent = useCallback((content: string): string => {
        if (!content) return content;
        
        // Remove FINAL ANSWER blocks (box drawing characters)
        let cleaned = content.replace(/╭[─\s]*FINAL ANSWER[─\s]*╮[\s\S]*?╰[─\s]*╯/g, '');
        
        // Remove [THINKING] ... patterns
        cleaned = cleaned.replace(/\[THINKING\][^\n]*(\n|$)/g, '');
        
        // Remove [TOOL_CALL] ... patterns
        cleaned = cleaned.replace(/\[TOOL_CALL\][^\n]*(\n|$)/g, '');
        
        // Remove [DELEGATE] ... patterns
        cleaned = cleaned.replace(/\[DELEGATE\][^\n]*(\n|$)/g, '');
        
        // Remove [RESPONSE] ... patterns
        cleaned = cleaned.replace(/\[RESPONSE\][^\n]*(\n|$)/g, '');
        
        // Remove multiple consecutive empty lines (more than 2)
        cleaned = cleaned.replace(/\n{3,}/g, '\n\n');
        
        // Remove leading/trailing whitespace
        cleaned = cleaned.trim();
        
        return cleaned;
    }, []);
    
    // Convert Vercel messages to our format
    const messages: Array<{
        id: string;
        role: "user" | "assistant";
        content: string;
    }> = vercelMessages.map((msg) => {
        const rawContent = typeof msg.content === "string" ? msg.content : "";
        // Clean content for assistant messages only
        const content = msg.role === "assistant" ? cleanContent(rawContent) : rawContent;
        return {
            id: msg.id,
            role: (msg.role === "user" ? "user" : "assistant") as "user" | "assistant",
            content,
        };
    });
    
    return {
        messages,
        input,
        setInput,
        setMessages,
        handleSubmit,
        append: vercelAppend,
        isLoading,
        stop,
        reload,
        currentAgent,
        thinkingSteps,
        activeToolCalls,
        agentDelegations,
        uiResources,
        isStreaming: isLoading || isProcessingRef.current,
        streamError,
    };
}

